/**
 * 📻 SPACE RADIO - Station Radio du Vaisseau
 * Design "Celestial Cabin VIP" - Ambiance spatiale
 * 
 * ✅ TOUTES les fonctionnalités conservées :
 * - Lecture/Pause
 * - Volume + Mute
 * - 8 stations radio
 * - Visualiseur audio animé
 * - Mode compact
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Play, Pause, Volume2, VolumeX, ChevronDown, ChevronUp, Waves } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from './ThemeToggle';

// Liste des stations radio françaises - URLs vérifiées et fonctionnelles
const RADIO_STATIONS = [
  { id: 'skyrock', name: 'Skyrock', genre: 'Hip-Hop/Rap', color: '#FF0000', streamUrl: 'https://icecast.skyrock.net/s/natio_mp3_128k', logo: '🎤' },
  { id: 'nova', name: 'Radio Nova', genre: 'Éclectique', color: '#FF6600', streamUrl: 'https://novazz.ice.infomaniak.ch/novazz-128.mp3', logo: '🌟' },
  { id: 'fip', name: 'FIP', genre: 'Éclectique', color: '#E91E63', streamUrl: 'https://icecast.radiofrance.fr/fip-hifi.aac', logo: '🎹' },
  { id: 'franceinter', name: 'France Inter', genre: 'Généraliste', color: '#E91E63', streamUrl: 'https://icecast.radiofrance.fr/franceinter-hifi.aac', logo: '📻' },
  { id: 'franceculture', name: 'France Culture', genre: 'Culture', color: '#9C27B0', streamUrl: 'https://icecast.radiofrance.fr/franceculture-hifi.aac', logo: '📚' },
  { id: 'francemusique', name: 'France Musique', genre: 'Classique', color: '#3F51B5', streamUrl: 'https://icecast.radiofrance.fr/francemusique-hifi.aac', logo: '🎻' },
  { id: 'mouv', name: 'Mouv\'', genre: 'Urban/Hip-Hop', color: '#00BCD4', streamUrl: 'https://icecast.radiofrance.fr/mouv-hifi.aac', logo: '🎧' },
  { id: 'franceinfo', name: 'France Info', genre: 'Info', color: '#2196F3', streamUrl: 'https://icecast.radiofrance.fr/franceinfo-hifi.aac', logo: '📰' },
];

export default function RadioPlayer({ compact = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStation, setCurrentStation] = useState(RADIO_STATIONS[0]);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [showStations, setShowStations] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handlePlay = async () => {
    if (!audioRef.current) return;
    
    try {
      setIsLoading(true);
      setError(null);
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.src = currentStation.streamUrl;
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Erreur lecture radio:', err);
      setError('Station indisponible');
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStationChange = async (station) => {
    setCurrentStation(station);
    setShowStations(false);
    setError(null);
    
    if (audioRef.current && isPlaying) {
      setIsLoading(true);
      audioRef.current.src = station.streamUrl;
      try {
        await audioRef.current.play();
      } catch (err) {
        console.error('Erreur changement station:', err);
        setError('Station indisponible');
        setIsPlaying(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Gestion des erreurs audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => {
      setError('Station indisponible');
      setIsPlaying(false);
      setIsLoading(false);
    };

    audio.addEventListener('error', handleError);
    return () => audio.removeEventListener('error', handleError);
  }, []);

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-2 p-2 rounded-xl border bg-slate-900/80 border-purple-500/30"
      >
        <audio ref={audioRef} />
        <Button
          onClick={handlePlay}
          size="sm"
          className="w-8 h-8 p-0 rounded-full"
          style={{ background: currentStation.color }}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate text-white">{currentStation.logo} {currentStation.name}</p>
          <p className="text-xs truncate text-gray-400">{currentStation.genre}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl h-full"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
        boxShadow: `0 0 40px ${currentStation.color}30, 0 20px 40px rgba(0,0,0,0.5)`,
        border: '1px solid rgba(148, 163, 184, 0.1)'
      }}
    >
      <audio ref={audioRef} />
      
      {/* 🌠 Background stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.4 + 0.1
            }}
            animate={{
              opacity: [0.1, 0.6, 0.1],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* HUD corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-purple-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-purple-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-pink-500/50" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-pink-500/50" />
      
      {/* Header */}
      <div className="relative p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `${currentStation.color}20`, border: `1px solid ${currentStation.color}40` }}
            >
              <motion.div
                animate={isPlaying ? { rotate: 360 } : {}}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                <Radio className="w-5 h-5" style={{ color: currentStation.color }} />
              </motion.div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                📻 Station Radio
              </h3>
              <p className="text-[10px] font-mono text-gray-500 tracking-wider">FRÉQUENCE SPATIALE</p>
            </div>
          </div>
          
          {/* Volume */}
          <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2 border border-white/5">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4 text-gray-500" />
              ) : (
                <Volume2 className="w-4 h-4 text-purple-400" />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-16 h-1 rounded-full appearance-none cursor-pointer"
              style={{ background: `linear-gradient(to right, ${currentStation.color} ${volume * 100}%, rgba(255,255,255,0.1) ${volume * 100}%)` }}
            />
          </div>
        </div>
      </div>

      {/* Station actuelle */}
      <div className="relative p-6">
        <div className="flex items-center gap-5">
          {/* Bouton Play - Holographique */}
          <div className="relative">
            <motion.div
              className="absolute -inset-2 rounded-full opacity-50"
              style={{ background: `radial-gradient(circle, ${currentStation.color}40 0%, transparent 70%)` }}
              animate={isPlaying ? { scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] } : {}}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <Button
              onClick={handlePlay}
              className="relative w-20 h-20 rounded-full p-0"
              style={{ 
                background: `linear-gradient(135deg, ${currentStation.color}, ${currentStation.color}80)`,
                boxShadow: isPlaying ? `0 0 40px ${currentStation.color}80, inset 0 0 20px rgba(255,255,255,0.2)` : 'none'
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-10 h-10" />
              ) : (
                <Play className="w-10 h-10 ml-1" />
              )}
            </Button>
          </div>
          
          <div className="flex-1">
            <motion.div
              key={currentStation.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <p className="text-3xl font-bold flex items-center gap-3 text-white">
                <span className="text-4xl">{currentStation.logo}</span>
                {currentStation.name}
              </p>
              <p className="text-gray-400 mt-1">{currentStation.genre}</p>
              {error && (
                <p className="text-red-500 text-sm mt-1">⚠️ {error}</p>
              )}
              
              {/* Visualiseur spatial */}
              {isPlaying && !error && (
                <motion.div
                  className="flex items-end gap-1 mt-3 h-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 rounded-full"
                      style={{ 
                        background: `linear-gradient(to top, ${currentStation.color}, ${currentStation.color}50)`,
                        boxShadow: `0 0 10px ${currentStation.color}50`
                      }}
                      animate={{
                        height: [4, Math.random() * 24 + 8, 4],
                      }}
                      transition={{
                        duration: 0.5,
                        repeat: Infinity,
                        delay: i * 0.08,
                        ease: "easeInOut"
                      }}
                    />
                  ))}
                  <span className="ml-3 text-xs text-gray-500 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    EN DIRECT
                  </span>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Sélecteur de stations */}
      <div className="border-t border-white/5">
        <button
          onClick={() => setShowStations(!showStations)}
          className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <span className="text-sm text-gray-400 flex items-center gap-2">
            <Waves className="w-4 h-4" />
            Changer de fréquence
          </span>
          <motion.div
            animate={{ rotate: showStations ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown className="w-5 h-5 text-gray-500" />
          </motion.div>
        </button>
        
        <AnimatePresence>
          {showStations && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-4 pt-0">
                {RADIO_STATIONS.map((station) => (
                  <motion.button
                    key={station.id}
                    onClick={() => handleStationChange(station)}
                    className={`relative p-3 rounded-xl text-left transition-all overflow-hidden ${
                      currentStation.id === station.id
                        ? 'ring-2 ring-white/50'
                        : 'hover:bg-white/5'
                    }`}
                    style={{ 
                      background: currentStation.id === station.id 
                        ? `linear-gradient(135deg, ${station.color}30, ${station.color}10)`
                        : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${station.color}30`
                    }}
                    whileHover={{ scale: 1.02, boxShadow: `0 0 20px ${station.color}20` }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{station.logo}</span>
                      <div>
                        <p className="font-semibold text-sm text-white">{station.name}</p>
                        <p className="text-[10px] text-gray-500">{station.genre}</p>
                      </div>
                    </div>
                    {currentStation.id === station.id && (
                      <motion.div
                        className="absolute top-1 right-1 w-2 h-2 rounded-full bg-green-500"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between">
        <span className="text-[9px] font-mono text-gray-600 tracking-widest">AUDIO DECK v2.0</span>
        <div className="flex gap-1">
          <div className="w-1 h-1 rounded-full" style={{ background: currentStation.color }} />
          <div className="w-1 h-1 rounded-full bg-purple-500" />
          <div className="w-1 h-1 rounded-full bg-pink-500" />
        </div>
      </div>
    </motion.div>
  );
}
